import { Icon } from 'components/common/Icon'
import { Logo } from 'components/common/Logo'
import { Separator } from 'components/common/Separator'
import useTranslation from 'next-translate/useTranslation'
import Link from 'next/link'
import { Fragment } from 'react'
import routes from '../../constants/routes'
import { LinkItem } from '../../types'
import { Button } from './Button'
import {
  useAuth,
  useAuthModal,
  useCurrencies,
  useDrawer,
  useOverlay,
} from 'contexts'
import {
  hasUserLiveListingsOrOffers,
  updateProfileCurrency,
} from 'api/profiles'
import { useIsMobile, useUserProfile } from 'hooks'
import { Currencies } from 'constants/currencies'

const integrations = [
  'stripe',
  'mastercard',
  'visa',
  'american_express',
  'apple',
  'dhl',
]

const collectLinks: LinkItem[] = [
  { label: 'artworks', href: routes.artworks },
  { label: 'artists', href: routes.artists },
  {
    label: 'buy',
    href: routes.artworks + '?availablities=Buy+now&sortBy=Recommended',
  },
  { label: 'sell', href: routes.sell },
]

const howItWorksLinks: LinkItem[] = [
  { label: 'overview', href: routes.howItWorks.base },
  { label: 'buyers', href: routes.howItWorks.forBuyers },
  { label: 'sellers', href: routes.howItWorks.forSellers },
  { label: 'artist_royalties', href: routes.howItWorks.forArtists },
  { label: 'shipping', href: routes.howItWorks.shipping },
  { label: 'authentication', href: routes.howItWorks.guarantee },
  { label: 'fees', href: routes.howItWorks.fees },
  {
    label: 'help_center',
    href: routes.intercom.base,
    target: '_blank',
  },
]

const legalLinks: LinkItem[] = [
  { label: 'terms_&_conditions', href: routes.termsAndConditions },
  { label: 'privacy_policy', href: routes.privacyPolicy },
  { label: 'cookie_policy', href: routes.cookiesPolicyTermly },
]

type CurrencyChangeProps = {
  currency: string
}

const CurrencyChangeModal = ({ currency }: CurrencyChangeProps) => {
  const { t } = useTranslation('single_artist')
  const { setIsOpen } = useOverlay()
  const isMobile = useIsMobile(1024)
  const { user } = useAuth()
  const { refetch: refetchUserProfile } = useUserProfile()

  const changeCurrency = async () => {
    if (!user) return

    const response = await updateProfileCurrency(user?.id, currency)

    if (!response.data) return
    refetchUserProfile()

    setIsOpen(false)
  }

  return (
    <div className='flex w-full max-w-[600px] flex-col rounded-smd bg-cool-50'>
      <header className='flex items-center justify-between border-b border-cool-300 p-6'>
        <div className='flex items-center space-x-2'>
          <Icon
            name='filled-alert-info'
            filled
            className='text-warning-default'
            size={24}
          />
          <h5 className='font-body text-base font-medium tracking-normal md:text-xl'>
            {t('common:currency_change')}
          </h5>
        </div>

        <button type='button' onClick={() => setIsOpen(false)}>
          <Icon name='close' className='text-cool-600' size={18} />
        </button>
      </header>
      <main className='px-6 py-4'>
        <div className='flex flex-col space-y-6 text-sm text-cool-700 '>
          {isMobile ? (
            <>
              <p className='text-sm font-normal'>
                {t('common:change_currency_text_1')}
              </p>
              <p className='text-sm font-normal'>
                {t('common:change_currency_text_2')}
              </p>
            </>
          ) : (
            <p className='text-sm font-normal'>
              {t('common:change_currency_text_3')}
            </p>
          )}
        </div>
      </main>
      <footer>
        <div className='flex flex-row justify-end border-t border-cool-300 px-6 py-4'>
          <Button
            id='cancel'
            className='mr-3 whitespace-nowrap'
            onClick={() => {
              setIsOpen(false)
            }}
            size={'lg'}
            style='solid-white'
          >
            {t('common:cancel')}
          </Button>
          <Button
            id='change_to'
            className='whitespace-nowrap'
            onClick={changeCurrency}
            size={'lg'}
          >
            {t('common:change_to')} {currency}
          </Button>
        </div>
      </footer>
    </div>
  )
}

const Currency = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { setIsOpen, setConfig } = useOverlay()
  const { userCurrency, setSelectedCurrency } = useCurrencies()
  const { refetch: refetchUserProfile } = useUserProfile()

  const selectedClasses = 'text-cool-800 bg-[#fff]'

  const handleCurrency = async (value: string) => {
    if (user) {
      const response = await hasUserLiveListingsOrOffers()
      if (response.error) return

      const hasLiveListingsOrOffers = response?.data?.hasListingOffersLive
      if (hasLiveListingsOrOffers) {
        // open modal
        setConfig({
          className: 'flex items-center px-5',
          body: <CurrencyChangeModal currency={value} />,
        })
        setIsOpen(true)
      } else {
        const response = await updateProfileCurrency(user?.id, value)

        if (!response.data) return
        refetchUserProfile()
      }
    } else {
      setSelectedCurrency(value)
    }
  }

  return (
    <div className='text-sm text-cool-400'>
      <p className='font-medium text-cool-150'>{t('common:currency')}</p>
      <div className='mt-1.5 flex max-w-[253px] items-center justify-around rounded-[1000px] bg-cool-700 px-1 py-1 font-medium'>
        <div
          className={`w-[33%] cursor-pointer rounded-[100px] py-1 text-center transition-all duration-500 ${
            userCurrency === 'GBP' ? selectedClasses : 'text-warning-light'
          }`}
          onClick={() => handleCurrency('GBP')}
        >
          £ (GBP)
        </div>

        {userCurrency === Currencies.EUR && (
          <div className='h-[15px] w-[1px] bg-cool-600 '></div>
        )}

        <div
          className={`w-[33%] cursor-pointer rounded-[100px] py-1 text-center transition-all duration-500 ${
            userCurrency === 'USD' ? selectedClasses : 'text-warning-light'
          }`}
          onClick={() => handleCurrency('USD')}
        >
          $ (USD)
        </div>

        {userCurrency === Currencies.GBP && (
          <div className='h-[15px] w-[1px]  bg-cool-600'></div>
        )}
        <div
          className={`w-[33%] cursor-pointer rounded-[100px] py-1 text-center transition-all duration-500 ${
            userCurrency === 'EUR' ? selectedClasses : 'text-warning-light'
          }`}
          onClick={() => handleCurrency('EUR')}
        >
          € (EUR)
        </div>
      </div>
    </div>
  )
}

export const Footer = () => {
  const { t } = useTranslation()

  const { setIsOpen: setIsAuthModalOpen, setState: setAuthModalState } =
    useAuthModal()
  const { setIsOpen } = useDrawer()

  return (
    <footer className='bg-cool-900 py-16'>
      <div className='container'>
        <div className='grid grid-cols-1 lg:grid-cols-4'>
          <div className='lg:col-span-1'>
            <Logo light />
            <p className='mb-6 mt-2 text-cool-150 lg:mb-24'>
              {t('common:buy_sell_discover')}{' '}
              <br className='hidden lg:block 2xl:hidden' />
              {t('common:give_back')}
            </p>
            <div className='flex w-fit flex-col md:flex-row md:items-center lg:block'>
              <ul className='-mx-1.5 -my-1.5 grid grid-cols-6'>
                {integrations.map(integration => (
                  <li key={integration} className='mx-1.5 my-1.5'>
                    <Icon custom name={integration} size={32} />
                  </li>
                ))}
              </ul>
            </div>
            <Separator className='my-8 block bg-cool-700 lg:hidden' />
          </div>
          <div className='col-span-3 grid grid-cols-2 md:grid-cols-3'>
            <div className='flex flex-col space-y-2 lg:col-span-1'>
              <p className='text-sm font-medium text-cool-150'>
                {t('common:collect')}
              </p>
              <nav>
                <ul className='flex flex-col space-y-2'>
                  {collectLinks.map((item, index) => (
                    <li key={index}>
                      <Link
                        href={item.href}
                        prefetch={false}
                        className='text-sm text-cool-400 hover:underline'
                      >
                        {t(`common:${item.label}`)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
            <div className='col-span-1 flex flex-col space-y-2'>
              <p className='text-sm font-medium text-cool-150'>
                {t('common:how_it_works')}
              </p>
              <nav>
                <ul className='flex flex-col space-y-2'>
                  {howItWorksLinks.map((item, index) => (
                    <li key={index}>
                      <Link
                        href={item.href}
                        prefetch={false}
                        className='text-sm text-cool-400 hover:underline'
                        target={item.target || '_self'}
                      >
                        {t(`common:${item.label}`)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>

            <div className='col-span-3 md:col-span-1'>
              <Separator className='my-8 bg-cool-700 md:hidden' />
              <Currency />
              <Separator className='my-8 bg-cool-700 ' />

              <p className='text-sm font-medium text-cool-150'>
                {t('common:join_newsletter')}
              </p>
              <p className='mb-6 mt-2 text-sm text-cool-400'>
                {t('common:subscribe')}
              </p>
              <Button
                pill
                className='w-full whitespace-nowrap'
                onClick={() => {
                  setIsOpen(false)
                  setIsAuthModalOpen(true)
                  setAuthModalState('register')
                }}
                size={'md'}
                style='solid-white'
                data-e2e='sign-up-button'
              >
                {t('common:sign_up')}
              </Button>

              <Separator className='my-8 bg-cool-700' />
              <div className='sm:hidden'>
                <Link
                  href={routes.invest.base}
                  className='mt-8 flex items-center space-x-1.5 text-sm text-cool-150'
                >
                  <span>{t('common:invest')}</span>

                  <Icon name='forward-arrow' />
                </Link>

                <p className='mt-1.5 text-sm text-cool-400'>
                  {t('common:were_redefining_the_art_market')}
                </p>
              </div>

              <div className='hidden sm:block'>
                <Link
                  href={routes.invest.base}
                  className='mt-8 flex items-center space-x-1.5 text-sm text-cool-150'
                >
                  <span>{t('common:invest')}</span>

                  <Icon name='forward-arrow' />
                </Link>

                <p className='mt-1.5 text-sm text-cool-400'>
                  {t('common:were_redefining_the_art_market')}
                </p>
              </div>
            </div>
          </div>
        </div>

        <Separator className='my-12 bg-cool-700' />

        <div className='flex flex-col-reverse items-center justify-between  text-sm text-cool-400 lg:flex-row'>
          <div className='flex flex-col-reverse items-center  lg:flex-row'>
            <p className='mt-5 text-center sm:mt-3 sm:text-left  lg:mr-3 lg:mt-0'>
              {t('common:copyright')} &copy; 2022 FairArt Ltd.{' '}
              {t('common:all_rights_reserved')}
            </p>
            <div className='sm:justify-left -mx-1.5 -my-1.5 flex flex-wrap justify-center'>
              {legalLinks.map(({ label, href }, index) => {
                return (
                  <Fragment key={href}>
                    <span
                      className={`my-1.5 hidden text-cool-700 ${
                        index === 0 ? 'lg:block' : 'sm:block'
                      }`}
                    >
                      |
                    </span>
                    <Link
                      href={href}
                      prefetch={false}
                      className='mx-1.5 my-1.5 hover:underline'
                      target='_blank'
                    >
                      {t(`common:${label}`)}
                    </Link>
                  </Fragment>
                )
              })}
            </div>
          </div>

          <Separator className='my-12 bg-cool-700 lg:my-0 lg:hidden' />

          <a
            href='https://www.instagram.com/fairart.io/'
            target='_blank'
            rel='noreferrer'
            className='-mx-1.5 -my-1.5 flex items-center hover:underline'
          >
            <p className='mx-1.5 my-1.5 text-sm text-cool-300'>@fairart.com</p>
            <Icon name='instagram' custom size={32} className='mx-1.5 my-1.5' />
          </a>
        </div>
      </div>
    </footer>
  )
}
